import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { SEOHead, SEO_CONFIG } from '../components/SEOHead';
import { 
  Phone, MessageCircle, ArrowRight, Filter, Search, Images, ZoomIn
} from 'lucide-react';
import { Input } from '../components/ui/input';
import { ImageGalleryModal } from '../components/ui/ImageGalleryModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Products = () => {
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [galleryModal, setGalleryModal] = useState({ open: false, product: null });

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await axios.get(`${API}/products`);
        setProducts(response.data);
        setFilteredProducts(response.data);
      } catch (error) {
        console.error('Error fetching products:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, []);

  useEffect(() => {
    let result = products;
    
    if (selectedCategory !== 'All') {
      result = result.filter(p => p.category === selectedCategory);
    }
    
    if (searchTerm) {
      result = result.filter(p => 
        p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.description.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    setFilteredProducts(result);
  }, [searchTerm, selectedCategory, products]);

  const categories = ['All', ...new Set(products.map(p => p.category))];

  const getWhatsAppUrl = (productName) => {
    return `https://wa.me/918512853955?text=${encodeURIComponent(`Hi, I am interested in ${productName}. Please share more details.`)}`;
  };

  const openGallery = (product) => {
    // Combine main image with additional images
    const allImages = [product.image, ...(product.images || [])].filter(Boolean);
    setGalleryModal({ open: true, product: { ...product, allImages } });
  };

  const closeGallery = () => {
    setGalleryModal({ open: false, product: null });
  };

  return (
    <div data-testid="products-page">
      <SEOHead {...SEO_CONFIG.products} />
      
      {/* Hero Section */}
      <section 
        className="relative pt-32 pb-20 md:pt-40 md:pb-28 bg-cover bg-center"
        style={{ backgroundImage: `url('https://images.unsplash.com/photo-1766215010263-0e4e9d7b3864?w=1920&q=80')` }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-slate-900/95 via-slate-900/85 to-slate-900/70"></div>
        <div className="relative max-w-7xl mx-auto px-4">
          <div className="max-w-3xl">
            <p className="text-orange-400 font-semibold mb-4">OUR PRODUCTS</p>
            <h1 className="font-['Oswald'] text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6">
              Quality Products at Best Prices
            </h1>
            <p className="text-lg text-slate-300 leading-relaxed mb-8">
              Browse our range of premium aluminium partitions, toughened glass products, and more. 
              All products come with quality assurance and professional installation.
            </p>
            <div className="flex flex-wrap gap-4">
              <a href="tel:08512853955">
                <Button size="lg" className="bg-orange-500 hover:bg-orange-600 text-white" data-testid="products-call-btn">
                  <Phone className="w-5 h-5 mr-2" />
                  Call for Bulk Orders
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Filters */}
      <section className="py-8 bg-slate-50 border-b sticky top-16 md:top-20 z-40">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
            <div className="flex items-center gap-4 w-full md:w-auto overflow-x-auto pb-2 md:pb-0">
              <Filter className="w-5 h-5 text-slate-500 flex-shrink-0" />
              {categories.map((category) => (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  data-testid={`category-${category.toLowerCase().replace(/\s+/g, '-')}`}
                  className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                    selectedCategory === category
                      ? 'bg-sky-500 text-white'
                      : 'bg-white text-slate-600 hover:bg-slate-100 border border-slate-200'
                  }`}
                >
                  {category}
                </button>
              ))}
            </div>
            <div className="relative w-full md:w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <Input
                type="text"
                placeholder="Search products..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                data-testid="product-search"
                className="pl-10"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Products Grid */}
      <section className="py-16 bg-white" data-testid="products-grid">
        <div className="max-w-7xl mx-auto px-4">
          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {[...Array(8)].map((_, i) => (
                <Card key={i} className="overflow-hidden">
                  <div className="aspect-square skeleton"></div>
                  <CardContent className="p-4">
                    <div className="h-4 w-1/3 skeleton rounded mb-2"></div>
                    <div className="h-5 w-2/3 skeleton rounded mb-3"></div>
                    <div className="h-8 w-1/2 skeleton rounded"></div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : filteredProducts.length === 0 ? (
            <div className="text-center py-16">
              <p className="text-slate-500 text-lg">No products found matching your criteria.</p>
              <Button 
                variant="outline" 
                className="mt-4"
                onClick={() => { setSearchTerm(''); setSelectedCategory('All'); }}
              >
                Clear Filters
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {filteredProducts.map((product) => (
                <Card 
                  key={product.id} 
                  className="product-card overflow-hidden group cursor-pointer"
                  data-testid={`product-card-${product.id}`}
                >
                  <div 
                    className="aspect-square overflow-hidden relative"
                    onClick={() => openGallery(product)}
                  >
                    <img 
                      src={product.image} 
                      alt={product.name}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                      loading="lazy"
                    />
                    <div className="absolute top-3 left-3">
                      <span className="px-3 py-1 bg-sky-500 text-white text-xs font-medium rounded-full">
                        {product.category}
                      </span>
                    </div>
                    {/* Image count badge */}
                    {(product.images?.length > 0) && (
                      <div className="absolute top-3 right-3">
                        <span className="px-2 py-1 bg-black/60 text-white text-xs font-medium rounded-full flex items-center gap-1">
                          <Images className="w-3 h-3" />
                          {1 + product.images.length}
                        </span>
                      </div>
                    )}
                    {/* Hover overlay */}
                    <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                      <div className="bg-white/90 rounded-full p-3">
                        <ZoomIn className="w-6 h-6 text-slate-800" />
                      </div>
                    </div>
                  </div>
                  <CardContent className="p-5">
                    <h3 
                      className="font-semibold text-slate-900 mb-2 line-clamp-2 min-h-[48px] cursor-pointer hover:text-sky-600 transition-colors"
                      onClick={() => openGallery(product)}
                    >
                      {product.name}
                    </h3>
                    <p className="text-slate-500 text-sm mb-3 line-clamp-2">{product.description}</p>
                    
                    <div className="flex items-baseline gap-1 mb-4">
                      <span className="font-['Oswald'] text-3xl font-bold text-sky-600">₹{product.price}</span>
                      <span className="text-slate-500 text-sm">/{product.price_unit}</span>
                    </div>

                    <div className="flex gap-2">
                      <Link to="/contact" state={{ product: product.name }} className="flex-1">
                        <Button className="w-full bg-orange-500 hover:bg-orange-600 text-white text-sm">
                          Enquire Now
                        </Button>
                      </Link>
                      <a href={getWhatsAppUrl(product.name)} target="_blank" rel="noopener noreferrer">
                        <Button variant="outline" size="icon" className="border-green-500 text-green-600 hover:bg-green-50">
                          <MessageCircle className="w-5 h-5" />
                        </Button>
                      </a>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Pricing Note */}
      <section className="py-12 bg-slate-50" data-testid="pricing-note">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <p className="text-slate-600">
            <strong className="text-slate-900">Note:</strong> Prices shown are starting prices and may vary based on 
            specifications, quantity, and installation requirements. Contact us for accurate quotation.
          </p>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-slate-900" data-testid="products-cta">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="font-['Oswald'] text-3xl md:text-4xl font-bold text-white mb-6">
            Can't Find What You're Looking For?
          </h2>
          <p className="text-slate-300 text-lg mb-8">
            We offer custom fabrication services. Share your requirements and we'll create the perfect solution.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a href="tel:08512853955">
              <Button size="lg" className="bg-orange-500 hover:bg-orange-600 text-white">
                <Phone className="w-5 h-5 mr-2" />
                Call: 085128 53955
              </Button>
            </a>
            <Link to="/contact">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10">
                Request Custom Product
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Image Gallery Modal */}
      <ImageGalleryModal
        isOpen={galleryModal.open}
        onClose={closeGallery}
        images={galleryModal.product?.allImages || []}
        title={galleryModal.product?.name || ''}
      />
    </div>
  );
};

export default Products;
